import type { BimActKc2PriceRowData } from 'gql/graphql';

import { useCallback, useMemo, useState } from 'react';

export interface IBimActKc2PriceRowData extends BimActKc2PriceRowData {
    isSectionHeader?: boolean;
}

export const useBimActKc2PricesGroupedData = (pricesData?: BimActKc2PriceRowData[] | null) => {
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const sections = useMemo(() => {
        if (!pricesData) {
            return [];
        }

        return pricesData?.reduce((acc: { sectionId: number; sectionName: string }[], price) => {
            if (!acc.find((v) => v.sectionId === price.sectionId)) {
                acc.push({ sectionId: price.sectionId, sectionName: price.sectionName || '' });
            }

            return acc;
        }, []);
    }, [pricesData]);

    const rowData = useMemo(
        () =>
            sections.reduce((acc: IBimActKc2PriceRowData[], section) => {
                acc = [
                    ...acc,
                    { ...section, name: section.sectionName, isSectionHeader: true } as IBimActKc2PriceRowData,
                    ...(pricesData?.filter(
                        (p) =>
                            p.sectionId === section.sectionId &&
                            (!filterValues?.name?.length || filterValues?.name?.includes(p.name)) &&
                            (!filterValues?.code?.length || filterValues?.code?.includes(p.code)),
                    ) || []),
                ];

                return acc;
            }, []),
        [filterValues?.code, filterValues?.name, pricesData, sections],
    );

    const filterData = useMemo(
        () => pricesData?.filter((p) => p.sectionId).map((p) => ({ name: p.name, code: p.code })) || [],
        [pricesData],
    );

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFilterValues((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues,
        }));
    }, []);

    return { rowData, changeFilterHandler, filterData };
};
